/**
 * ObjectDetailsPanel — treść zakładek obiektu: Właściwości / SQL / Statystyki / Zależności.
 * Wszystko tylko do odczytu; „SQL" to skrypt utworzenia obiektu (podgląd, kopiowanie,
 * otwarcie w Query Tool).
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { LazySqlEditor } from '@/components/ui/LazySqlEditor';
import { fetchDbObject } from '@/lib/db-browser/api';
import type { DbKeyValue, DbObjectRef } from '@/lib/db-browser/types';

export type ObjectPanel = 'props' | 'sql' | 'stats' | 'deps';

interface ObjectDetailsPanelProps {
  tenantId: string;
  objectRef: DbObjectRef | null;
  panel: ObjectPanel;
  onOpenInQueryTool: (sql: string) => void;
  onCopy: (text: string) => void;
}

function KeyValueTable({ rows, label }: { rows: DbKeyValue[]; label: string }) {
  return (
    <table className="w-full max-w-3xl border-collapse text-[13px]" aria-label={label}>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-border-secondary">
            <th scope="row" className="w-64 px-3 py-2 text-left font-medium text-text-secondary">
              {r.key}
            </th>
            <td className="px-3 py-2 font-mono text-xs text-text-primary">{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ObjectDetailsPanel({
  tenantId,
  objectRef,
  panel,
  onOpenInQueryTool,
  onCopy,
}: ObjectDetailsPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'tenant-db', tenantId, 'object', objectRef],
    queryFn: () => fetchDbObject(tenantId, objectRef!),
    enabled: !!objectRef,
    staleTime: 60_000,
  });

  if (!objectRef) {
    return <p className="p-6 text-sm text-text-tertiary">{t('admin.dbBrowser.object.pick')}</p>;
  }
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-6">
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6">
        <Alert tone="error" title={t('admin.dbBrowser.object.loadFailed')} />
      </div>
    );
  }

  if (panel === 'props') {
    return (
      <div className="h-full overflow-auto p-5">
        <KeyValueTable rows={data.properties} label={t('admin.dbBrowser.tabs.properties')} />
        <p className="mt-3 text-xs text-text-tertiary">
          {t('admin.dbBrowser.object.readOnlyNote')}
        </p>
      </div>
    );
  }

  if (panel === 'sql') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center gap-2 border-b border-border-secondary px-3 py-1.5">
          <span className="text-xs text-text-secondary">
            {t('admin.dbBrowser.object.ddlCaption')}
          </span>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => onCopy(data.ddl)}>
            {t('admin.dbBrowser.object.copy')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onOpenInQueryTool(data.ddl)}>
            {t('admin.dbBrowser.object.openInQueryTool')}
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          <LazySqlEditor
            value={data.ddl}
            onChange={() => undefined}
            readOnly
            height="100%"
            aria-label={t('admin.dbBrowser.object.ddlCaption')}
          />
        </div>
      </div>
    );
  }

  if (panel === 'stats') {
    return (
      <div className="h-full overflow-auto p-5">
        {data.stats ? (
          <KeyValueTable rows={data.stats} label={t('admin.dbBrowser.tabs.statistics')} />
        ) : (
          <p className="text-sm text-text-tertiary">{t('admin.dbBrowser.object.noStats')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-5">
      {data.dependencies.length === 0 ? (
        <p className="text-sm text-text-tertiary">{t('admin.dbBrowser.object.noDependencies')}</p>
      ) : (
        <table className="w-full max-w-4xl border-collapse text-[13px]">
          <thead>
            <tr className="bg-bg-secondary text-left">
              <th scope="col" className="border border-border-secondary px-3 py-1.5 font-semibold">
                {t('admin.dbBrowser.object.depType')}
              </th>
              <th scope="col" className="border border-border-secondary px-3 py-1.5 font-semibold">
                {t('admin.dbBrowser.object.depName')}
              </th>
              <th scope="col" className="border border-border-secondary px-3 py-1.5 font-semibold">
                {t('admin.dbBrowser.object.depRelation')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.dependencies.map((d) => (
              <tr key={`${d.type}-${d.name}`}>
                <td className="border border-border-secondary px-3 py-1.5">{d.type}</td>
                <td className="border border-border-secondary px-3 py-1.5 font-mono text-xs">
                  {d.name}
                </td>
                <td className="border border-border-secondary px-3 py-1.5">{d.relation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
